import { createApp } from "vue";
import App from "./App.vue";
import Keycloak from "keycloak-js/dist/keycloak";
import { Language, loadI18n, selectedLocale } from "./locales/i18n";

import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";

import "./assets/scss/main.scss";

//Plugins
import PrimeVue from "./plugins/primevue";
import Router from "./plugins/router";

const app = createApp(App);
app.config.globalProperties.isAdmin = true;
app.config.globalProperties.appLoading = true;
app.config.globalProperties.dateFormat = "dd.mm.yy";
app.config.globalProperties.showAccountNotConfirmedPopup = false;
app.config.globalProperties.googleMapsTheme = [
  {
    featureType: "administrative.province",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  {
    featureType: "poi.park",
    elementType: "labels.text",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "road.arterial", stylers: [{ visibility: "off" }] },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.local",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
];

const enLocale = {
  startsWith: "Starts with",
  contains: "Contains",
  notContains: "Not contains",
  endsWith: "Ends with",
  equals: "Equals",
  notEquals: "Not equals",
  noFilter: "No Filter",
  lt: "Less than",
  lte: "Less than or equal to",
  gt: "Greater than",
  gte: "Greater than or equal to",
  dateIs: "Date is",
  dateIsNot: "Date is not",
  dateBefore: "Date is before",
  dateAfter: "Date is after",
  clear: "Clear",
  apply: "Apply",
  matchAll: "Match All",
  matchAny: "Match Any",
  addRule: "Add Rule",
  removeRule: "Remove Rule",
  accept: "Yes",
  reject: "No",
  choose: "Choose",
  upload: "Upload",
  cancel: "Cancel",
  completed: "Completed",
  pending: "Pending",
  dayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  chooseYear: "Choose Year",
  chooseMonth: "Choose Month",
  chooseDate: "Choose Date",
  prevDecade: "Previous Decade",
  nextDecade: "Next Decade",
  prevYear: "Previous Year",
  nextYear: "Next Year",
  prevMonth: "Previous Month",
  nextMonth: "Next Month",
  prevHour: "Previous Hour",
  nextHour: "Next Hour",
  prevMinute: "Previous Minute",
  nextMinute: "Next Minute",
  prevSecond: "Previous Second",
  nextSecond: "Next Second",
  am: "am",
  pm: "pm",
  today: "Today",
  weekHeader: "Wk",
  firstDayOfWeek: 0,
  dateFormat: "mm/dd/yy",
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  passwordPrompt: "Enter a password",
  emptyFilterMessage: "No results found", // @deprecated Use 'emptySearchMessage' option instead.
  searchMessage: "{0} results are available",
  selectionMessage: "{0} items selected",
  emptySelectionMessage: "No selected item",
  emptySearchMessage: "No results found",
  emptyMessage: "No available options",
  aria: {
    trueLabel: "True",
    falseLabel: "False",
    nullLabel: "Not Selected",
    star: "1 star",
    stars: "{star} stars",
    selectAll: "All items selected",
    unselectAll: "All items unselected",
    close: "Close",
    previous: "Previous",
    next: "Next",
    navigation: "Navigation",
    scrollTop: "Scroll Top",
    moveTop: "Move Top",
    moveUp: "Move Up",
    moveDown: "Move Down",
    moveBottom: "Move Bottom",
    moveToTarget: "Move to Target",
    moveToSource: "Move to Source",
    moveAllToTarget: "Move All to Target",
    moveAllToSource: "Move All to Source",
    pageLabel: "{page}",
    firstPageLabel: "First Page",
    lastPageLabel: "Last Page",
    nextPageLabel: "Next Page",
    prevPageLabel: "Previous Page",
    rowsPerPageLabel: "Rows per page",
    previousPageLabel: "Previous Page",
    jumpToPageDropdownLabel: "Jump to Page Dropdown",
    jumpToPageInputLabel: "Jump to Page Input",
    selectRow: "Row Selected",
    unselectRow: "Row Unselected",
    expandRow: "Row Expanded",
    collapseRow: "Row Collapsed",
    showFilterMenu: "Show Filter Menu",
    hideFilterMenu: "Hide Filter Menu",
    filterOperator: "Filter Operator",
    filterConstraint: "Filter Constraint",
    editRow: "Row Edit",
    saveEdit: "Save Edit",
    cancelEdit: "Cancel Edit",
    listView: "List View",
    gridView: "Grid View",
    slide: "Slide",
    slideNumber: "{slideNumber}",
    zoomImage: "Zoom Image",
    zoomIn: "Zoom In",
    zoomOut: "Zoom Out",
    rotateRight: "Rotate Right",
    rotateLeft: "Rotate Left",
  },
};

const slLocale = {
  startsWith: "Starts with",
  contains: "Contains",
  notContains: "Not contains",
  endsWith: "Ends with",
  equals: "Equals",
  notEquals: "Not equals",
  noFilter: "No Filter",
  lt: "Less than",
  lte: "Less than or equal to",
  gt: "Greater than",
  gte: "Greater than or equal to",
  dateIs: "Date is",
  dateIsNot: "Date is not",
  dateBefore: "Date is before",
  dateAfter: "Date is after",
  clear: "Clear",
  apply: "Apply",
  matchAll: "Match All",
  matchAny: "Match Any",
  addRule: "Add Rule",
  removeRule: "Remove Rule",
  accept: "Yes",
  reject: "No",
  choose: "Choose",
  upload: "Upload",
  cancel: "Cancel",
  completed: "Completed",
  pending: "Pending",
  dayNames: [
    "Nedelja",
    "Ponedeljek",
    "Torek",
    "Sreda",
    "Četrtek",
    "Petek",
    "Sobota",
  ],
  dayNamesShort: ["Ned", "Pon", "Tor", "Sre", "Čet", "Pet", "Sob"],
  dayNamesMin: ["Ne", "Po", "To", "Sr", "Če", "Pe", "So"],
  monthNames: [
    "Januar",
    "Februar",
    "Marec",
    "April",
    "Maj",
    "Juni",
    "Julij",
    "August",
    "September",
    "Oktober",
    "November",
    "December",
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Maj",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  chooseYear: "Izberi leto",
  chooseMonth: "Izberi mesec",
  chooseDate: "Izberi datum",
  prevDecade: "Prejšnje obdobje",
  nextDecade: "Naslednje obdobje",
  prevYear: "Prejšnje leto",
  nextYear: "Nasledno leto",
  prevMonth: "Prejšnji mesec",
  nextMonth: "Naslednji mesec",
  prevHour: "Prejšnja ura",
  nextHour: "Naslednja ura",
  prevMinute: "Prejšnja minuta",
  nextMinute: "Naslednja minuta",
  prevSecond: "Prejšnja sekunda",
  nextSecond: "Naslednja sekunda",
  am: "am",
  pm: "pm",
  today: "Dane",
  weekHeader: "Te",
  firstDayOfWeek: 1,
  dateFormat: "dd/mm/yy",
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  passwordPrompt: "Enter a password",
  emptyFilterMessage: "No results found", // @deprecated Use 'emptySearchMessage' option instead.
  searchMessage: "{0} results are available",
  selectionMessage: "{0} items selected",
  emptySelectionMessage: "No selected item",
  emptySearchMessage: "Ni najdenih rezultatov",
  emptyMessage: "Ni razpoložljivih možnosti", //"No available options",
  aria: {
    trueLabel: "True",
    falseLabel: "False",
    nullLabel: "Not Selected",
    star: "1 star",
    stars: "{star} stars",
    selectAll: "All items selected",
    unselectAll: "All items unselected",
    close: "Close",
    previous: "Previous",
    next: "Next",
    navigation: "Navigation",
    scrollTop: "Scroll Top",
    moveTop: "Move Top",
    moveUp: "Move Up",
    moveDown: "Move Down",
    moveBottom: "Move Bottom",
    moveToTarget: "Move to Target",
    moveToSource: "Move to Source",
    moveAllToTarget: "Move All to Target",
    moveAllToSource: "Move All to Source",
    pageLabel: "{page}",
    firstPageLabel: "First Page",
    lastPageLabel: "Last Page",
    nextPageLabel: "Next Page",
    prevPageLabel: "Previous Page",
    rowsPerPageLabel: "Rows per page",
    previousPageLabel: "Previous Page",
    jumpToPageDropdownLabel: "Jump to Page Dropdown",
    jumpToPageInputLabel: "Jump to Page Input",
    selectRow: "Row Selected",
    unselectRow: "Row Unselected",
    expandRow: "Row Expanded",
    collapseRow: "Row Collapsed",
    showFilterMenu: "Show Filter Menu",
    hideFilterMenu: "Hide Filter Menu",
    filterOperator: "Filter Operator",
    filterConstraint: "Filter Constraint",
    editRow: "Row Edit",
    saveEdit: "Save Edit",
    cancelEdit: "Cancel Edit",
    listView: "List View",
    gridView: "Grid View",
    slide: "Slide",
    slideNumber: "{slideNumber}",
    zoomImage: "Zoom Image",
    zoomIn: "Zoom In",
    zoomOut: "Zoom Out",
    rotateRight: "Rotate Right",
    rotateLeft: "Rotate Left",
  },
};

app.use(PrimeVue, {
  ripple: true,
  inputStyle: "outlined",
  locale: selectedLocale() === Language.en ? enLocale : slLocale,
});

app.use(Router);
app.use(loadI18n);

const { setAuth } = UserRepositoryAPI();

function tokenInterceptor() {
  axios.interceptors.request.use(
    async (config: any) => {
      try {
        if (keycloak.isTokenExpired()) {
          const refreshed = await keycloak.updateToken(5);
          if (!refreshed) {
            keycloak.logout({ redirectUri: window.origin });
          }
        }
      } catch (e) {
        // Keycloak has this nasty habit of throwing errors if you call methods when you shouldn't
        // For example if user isn't logged in and you try to refresh token, Keycloak will throw
        // and on top of this, it will throw simple string instead of error. Completely useless
        console.info(e);
      }

      if (keycloak.authenticated) {
        setAuth(keycloak);
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

function responseInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401 && keycloak.logout) {
        keycloak.logout({ redirectUri: window.origin });
      }
      return Promise.reject(error);
    }
  );
}
Router.beforeEach((to, from, next) => {
  app.config.globalProperties.appLoading = true;
  if (to.meta.isAuthenticated) {
    const roles = to.meta.roles as [];
    if (!keycloak.authenticated) {
      // The page is protected and the user is not authenticated. Force a login.
      keycloak.login({ redirectUri: window.origin });
    }
    const userAuthorized = roles.some((role) => keycloak.hasResourceRole(role));
    if (userAuthorized) {
      next();
    } else {
      // The user was authenticated, but did not have the correct role
      // Redirect to an unauthorized page
      next({ name: "unauthorized" });
    }
  } else {
    // Page did not require authentication
    next();
  }
});
Router.afterEach(() => {
  app.config.globalProperties.appLoading = false;
  scrollToTop();
});

function scrollToTop() {
  document.getElementById("app_id")?.scrollIntoView({ behavior: "smooth" });
}

//Components
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import InputPassword from "primevue/password";
import MultiSelect from "primevue/multiselect";
import AutoComplete from "primevue/autocomplete";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Calendar from "primevue/calendar";
import RadioButton from "primevue/radiobutton";
import PrimeDialog from "primevue/dialog";
import Checkbox from "primevue/checkbox";
import Menu from "primevue/menu";
import PanelMenu from "primevue/panelmenu";
import Menubar from "primevue/menubar";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Breadcrumb from "primevue/breadcrumb";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Textarea from "primevue/textarea";
import Loader from "@/components/loader.vue";
import InfoCircle from "@/components/info-circle.vue";
import Document from "@/components/document.vue";
import Title from "@/components/title.vue";
import Section from "@/components/section.vue";
import AdminSection from "@/components/admin-section.vue";
import Banner from "@/components/banner.vue";
import UserBanner from "@/components/user-banner.vue";
import IconTextBlock from "@/components/icon-text-block.vue";
import ProductPreview from "@/components/product-preview.vue";
import ProductFull from "@/components/product-full.vue";
import AddToCompare from "@/components/add-to-compare.vue";
import GoogleMapsSmall from "@/components/google-maps-small.vue";
import SimpleLayout from "@/layout/simple-layout.vue";
import Paginator from "primevue/paginator";
import Message from "primevue/message";
import Tree from "primevue/tree";
import Image from "primevue/image";
import Prompt from "@/components/prompt.vue";
import Dialog from "@/components/dialog.vue";
import Login from "@/components/login.vue";
import Register from "@/components/register.vue";
import GDPRConsent from "@/components/gdpr-consent.vue";
import AccountNotConfirmed from "@/components/account-not-confirmed.vue";
import UserCard from "@/components/user-card.vue";
import HelpCard from "@/components/help-card.vue";
import AvatarUpload from "@/components/avatar-upload.vue";
import FileUpload from "primevue/fileupload";
import Listbox from "primevue/listbox";
import Carousel from "primevue/carousel";

import ResearchEquipmentPreview from "@/modules/research-equipment/components/research-equipment-preview.vue";
import ResearchEquipmentFormView from "@/modules/research-equipment/components/research-equipment-form-view.vue";
import ResearchEquipmentDataTableView from "@/modules/research-equipment/components/research-equipment-data-table-view.vue";
import ResearchEquipmentDataTableComparisonView from "@/modules/research-equipment/components/research-equipment-data-table-comparison-view.vue";
import ResearchEquipmentDetailView from "@/modules/research-equipment/components/research-equipment-detail-view.vue";
import ResearchEquipmentCompare from "@/modules/research-equipment/components/research-equipment-compare.vue";

import ResearchOrganizationDataTableView from "@/modules/research-organization/components/research-organization-data-table-view.vue";
import ResearchOrganizationFormView from "@/modules/research-organization/components/research-organization-form-view.vue";

import ResearchInfrastructurePreview from "@/modules/research-infrastructure/components/research-infrastructure-preview.vue";
import ResearchInfrastructureDataTableView from "@/modules/research-infrastructure/components/research-infrastructure-data-table-view.vue";
import ResearchInfrastructureFormView from "@/modules/research-infrastructure/components/research-infrastructure-form-view.vue";

import ResearchGroupDataTableView from "@/modules/research-group/components/research-group-data-table-view.vue";
import ResearchGroupFormView from "@/modules/research-group/components/research-group-form-view.vue";

import Accessibility from "./modules/accessibility/components/accessibility.vue";
import "./modules/accessibility/assets/scss/main.scss";

import Toast from "primevue/toast";
import ToastService from "primevue/toastservice";

app.use(ToastService);
app.use(ToastService).provide("toast", app.config.globalProperties.$toast);
app.provide("appLoading", app.config.globalProperties.appLoading);

import ConfirmDialog from "primevue/confirmdialog";
import ConfirmationService from "primevue/confirmationservice";
import UserRepositoryAPI from "./modules/user/user-repository-api";
import axios from "@/services/axios";

app.use(ConfirmationService);

app.component("Button", Button);
app.component("InputText", InputText);
app.component("Textarea", Textarea);
app.component("InputNumber", InputNumber);
app.component("InputPassword", InputPassword);
app.component("MultiSelect", MultiSelect);
app.component("DataTable", DataTable);
app.component("Column", Column);
app.component("Toast", Toast);
app.component("RadioButton", RadioButton);
app.component("Calendar", Calendar);
app.component("AutoComplete", AutoComplete);
app.component("Loader", Loader);
app.component("Dropdown", Dropdown);
app.component("InputSwitch", InputSwitch);
app.component("ConfirmDialog", ConfirmDialog);
app.component("PrimeDialog", PrimeDialog);
app.component("Menu", Menu);
app.component("Menubar", Menubar);
app.component("PanelMenu", PanelMenu);
app.component("Checkbox", Checkbox);
app.component("TabView", TabView);
app.component("TabPanel", TabPanel);
app.component("Breadcrumb", Breadcrumb);
app.component("Accordion", Accordion);
app.component("AccordionTab", AccordionTab);
app.component("InfoCircle", InfoCircle);
app.component("Title", Title);
app.component("Section", Section);
app.component("AdminSection", AdminSection);
app.component("Banner", Banner);
app.component("UserBanner", UserBanner);
app.component("Paginator", Paginator);
app.component("Document", Document);
app.component("GoogleMapsSmall", GoogleMapsSmall);
app.component("SimpleLayout", SimpleLayout);
app.component("Message", Message);
app.component("Tree", Tree);
app.component("ProductPreview", ProductPreview);
app.component("ProductFull", ProductFull);
app.component("IconTextBlock", IconTextBlock);
app.component("FileUpload", FileUpload);
app.component("Image", Image);
app.component("AddToCompare", AddToCompare);
app.component("Prompt", Prompt);
app.component("Dialog", Dialog);
app.component("Login", Login);
app.component("Register", Register);
app.component("GDPRConsent", GDPRConsent);
app.component("AccountNotConfirmed", AccountNotConfirmed);
app.component("UserCard", UserCard);
app.component("HelpCard", HelpCard);
app.component("ResearchEquipmentPreview", ResearchEquipmentPreview);
app.component("ResearchEquipmentFormView", ResearchEquipmentFormView);
app.component("ResearchEquipmentDataTableView", ResearchEquipmentDataTableView);
app.component(
  "ResearchEquipmentDataTableComparisonView",
  ResearchEquipmentDataTableComparisonView
);
app.component(
  "ResearchOrganizationDataTableView",
  ResearchOrganizationDataTableView
);
app.component(
  "ResearchInfrastructureDataTableView",
  ResearchInfrastructureDataTableView
);
app.component("ResearchGroupDataTableView", ResearchGroupDataTableView);

app.component("ResearchInfrastructurePreview", ResearchInfrastructurePreview);
app.component("ResearchOrganizationFormView", ResearchOrganizationFormView);
app.component("ResearchInfrastructureFormView", ResearchInfrastructureFormView);
app.component("ResearchGroupFormView", ResearchGroupFormView);
app.component("ResearchEquipmentDetailView", ResearchEquipmentDetailView);
app.component("ResearchEquipmentCompare", ResearchEquipmentCompare);
app.component("Listbox", Listbox);
app.component("AvatarUpload", AvatarUpload);
app.component("Carousel", Carousel);
app.component("Accessibility", Accessibility);

declare let window: Window & {
  VUE_APP_KEYCLOAK_URL: string;
  VUE_APP_KEYCLOAK_REALM: string;
  VUE_APP_KEYCLOAK_CLIENT_ID: string;
  VUE_APP_SENTRY_DSN: string;
};

const keycloak = Keycloak({
  url: window["VUE_APP_KEYCLOAK_URL"] || process.env.VUE_APP_KEYCLOAK_URL,
  realm: window["VUE_APP_KEYCLOAK_REALM"] || process.env.VUE_APP_KEYCLOAK_REALM,
  clientId:
    window["VUE_APP_KEYCLOAK_CLIENT_ID"] ||
    process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
});

app.config.globalProperties.$kc = keycloak;
app.provide("keycloak", app.config.globalProperties.$kc);

keycloak
  .init({
    responseMode: "query",
    checkLoginIframe: false,
    onLoad: "check-sso",
  })
  .then(async (auth) => {
    tokenInterceptor();
    responseInterceptor();
    app.mount("#app");

    if (auth) {
      localStorage.isUserLoggedIn = true;
    }
  })
  .catch((e) =>
    console.error("Unhandled exception during application load", e)
  );

/*Sentry.init({
  app,
  dsn: window["VUE_APP_SENTRY_DSN"] || process.env.VUE_APP_SENTRY_DSN,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(Router),
      tracingOrigins: ["localhost", /^\//],
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});*/
